export const TOOL_GROUPS = [
  {
    id: "mcp-tools-search",
    title: "Search & Geocoding",
    tools: [
      {
        name: "address___search",
        category: "Search",
        categoryKey: "search",
        description:
          "Search for addresses or points of interest using free text — building names, road names, bus stop numbers, or postal codes. Supports pagination up to 100 pages.",
        params: [
          {
            name: "searchVal",
            required: true,
            parts: [
              {
                t: "text",
                v: "Building name, road, bus stop number, or postal code",
              },
            ],
          },
          {
            name: "returnGeom",
            required: true,
            parts: [
              { t: "text", v: "Return geometry coordinates?" },
              { t: "chip", v: "Y" },
              { t: "text", v: "or" },
              { t: "chip", v: "N" },
            ],
          },
          {
            name: "getAddrDetails",
            required: true,
            parts: [
              { t: "text", v: "Return full address details?" },
              { t: "chip", v: "Y" },
              { t: "text", v: "or" },
              { t: "chip", v: "N" },
            ],
          },
          {
            name: "pageNum",
            required: false,
            parts: [
              { t: "text", v: "Page number (1-100) for paginated results" },
            ],
          },
        ],
        example: `{
  "searchVal": "Marina Bay Sands",
  "returnGeom": "Y",
  "getAddrDetails": "Y",
  "pageNum": 1
}`,
      },
      {
        name: "reversegeocode___address_from_latlng",
        category: "Geocoding",
        categoryKey: "geocoding",
        description:
          "Look up the address at a given WGS84 latitude/longitude coordinate. Optionally filter results to HDB addresses only and set a search radius buffer.",
        params: [
          {
            name: "location",
            required: true,
            parts: [
              { t: "text", v: "WGS84 coordinate as" },
              { t: "chip", v: '"lat,lng"' },
              { t: "text", v: "e.g." },
              { t: "chip", v: "1.3521,103.8198" },
            ],
          },
          {
            name: "addressType",
            required: false,
            parts: [
              { t: "chip", v: "all" },
              { t: "text", v: "(default) or" },
              { t: "chip", v: "hdb" },
            ],
          },
          {
            name: "buffer",
            required: false,
            parts: [{ t: "text", v: "Search radius in metres, max 500" }],
          },
        ],
        example: `{
  "location": "1.2833,103.8607",
  "addressType": "all",
  "buffer": 100
}`,
      },
      {
        name: "reversegeocode___address_from_xy",
        category: "Geocoding",
        categoryKey: "geocoding",
        description:
          "Same as reversegeocode___address_from_latlng but accepts Singapore SVY21 X,Y coordinates instead of WGS84 lat/lng. Useful when working with Singapore-native coordinate systems.",
        params: [
          {
            name: "location",
            required: true,
            parts: [
              { t: "text", v: "SVY21 coordinate as" },
              { t: "chip", v: '"x,y"' },
            ],
          },
          {
            name: "addressType",
            required: false,
            parts: [
              { t: "chip", v: "all" },
              { t: "text", v: "or" },
              { t: "chip", v: "hdb" },
            ],
          },
          {
            name: "buffer",
            required: false,
            parts: [{ t: "text", v: "Search radius in metres, max 500" }],
          },
        ],
        example: `{
  "location": "30404.6,30287.1",
  "addressType": "all",
  "buffer": 100
}`,
      },
    ],
  },
  {
    id: "mcp-tools-coordinates",
    title: "Coordinate Conversion",
    tools: [
      {
        kind: "family",
        name: "coordinates___convert*",
        category: "Coordinates",
        categoryKey: "coordinates",
        description: [
          {
            t: "text",
            v: "Six tools that convert a coordinate between the three systems used in Singapore data: WGS84 (EPSG:4326, lat/lng), SVY21 (EPSG:3414) and Web Mercator (EPSG:3857). Pick the tool whose name matches your ",
          },
          { t: "em", v: "input" },
          { t: "text", v: " system. All parameters are required numbers." },
        ],
        columns: [
          { key: "name", label: "Tool", width: "40%" },
          { key: "converts", label: "Converts", width: "34%" },
          { key: "params", label: "Parameters", width: "26%" },
        ],
        rows: [
          {
            name: "coordinates___convert4326To3414",
            converts: "WGS84 lat/lng \u2192 SVY21 X/Y",
            params: ["latitude", "longitude"],
          },
          {
            name: "coordinates___convert4326To3857",
            converts: "WGS84 lat/lng \u2192 EPSG:3857 X/Y",
            params: ["latitude", "longitude"],
          },
          {
            name: "coordinates___convert3414To4326",
            converts: "SVY21 X/Y \u2192 WGS84 lat/lng",
            params: ["X", "Y"],
          },
          {
            name: "coordinates___convert3414To3857",
            converts: "SVY21 X/Y \u2192 EPSG:3857 X/Y",
            params: ["X", "Y"],
          },
          {
            name: "coordinates___convert3857To4326",
            converts: "EPSG:3857 X/Y \u2192 WGS84 lat/lng",
            params: ["X", "Y"],
          },
          {
            name: "coordinates___convert3857To3414",
            converts: "EPSG:3857 X/Y \u2192 SVY21 X/Y",
            params: ["X", "Y"],
          },
        ],
        callout: {
          title: "Watch out for",
          parts: [
            { t: "text", v: "SVY21 " },
            { t: "chip", v: "X" },
            { t: "text", v: " and " },
            { t: "chip", v: "Y" },
            {
              t: "text",
              v: " ranges overlap in Singapore, so a swapped pair often returns a valid but wrong location instead of an error. Lat/lng inputs are also checked against Singapore\u2019s bounds, and points outside them are rejected with a 400 error.",
            },
          ],
        },
        exampleTool: "coordinates___convert4326To3414",
        example: `{
  "latitude": 1.2833,
  "longitude": 103.8607
}`,
      },
    ],
  },
  {
    id: "mcp-tools-routing",
    title: "Routing",
    tools: [
      {
        name: "routing___transport",
        category: "Routing",
        categoryKey: "routing",
        wide: true,
        description:
          "Generate turn-by-turn routes between two coordinates. Supports public transport, driving, walking, and cycling routes. Returns route legs, duration, distance, and geometry.",
        params: [
          {
            name: "routeType",
            required: true,
            parts: [
              { t: "chip", v: "pt" },
              { t: "text", v: "\u00b7" },
              { t: "chip", v: "drive" },
              { t: "text", v: "\u00b7" },
              { t: "chip", v: "walk" },
              { t: "text", v: "\u00b7" },
              { t: "chip", v: "cycle" },
            ],
          },
          {
            name: "date",
            required: false,
            parts: [
              { t: "chip", v: "MM-DD-YYYY" },
              { t: "text", v: "format, required for" },
              { t: "chip", v: "pt" },
            ],
          },
          {
            name: "start",
            required: true,
            parts: [
              { t: "text", v: "Origin as" },
              { t: "chip", v: '"lat,lng"' },
            ],
          },
          {
            name: "time",
            required: false,
            parts: [
              { t: "chip", v: "HH:MM:SS" },
              { t: "text", v: "24-hour, required for" },
              { t: "chip", v: "pt" },
            ],
          },
          {
            name: "end",
            required: true,
            parts: [
              { t: "text", v: "Destination as" },
              { t: "chip", v: '"lat,lng"' },
            ],
          },
          {
            name: "maxWalkDistance",
            required: false,
            parts: [
              { t: "text", v: "Max walking distance in metres for" },
              { t: "chip", v: "pt" },
              { t: "text", v: "routing" },
            ],
          },
          {
            name: "mode",
            required: false,
            parts: [
              { t: "text", v: "Required for routeType" },
              { t: "chip", v: "pt" },
              { t: "text", v: ":" },
              { t: "chip", v: "transit" },
              { t: "text", v: "\u00b7" },
              { t: "chip", v: "bus" },
              { t: "text", v: "\u00b7" },
              { t: "chip", v: "rail" },
            ],
          },
          {
            name: "numItineraries",
            required: false,
            parts: [{ t: "text", v: "Number of route options to return" }],
          },
        ],
        example: `{
  "routeType": "pt",
  "start": "1.2990,103.7870",
  "end": "1.2833,103.8607",
  "mode": "transit",
  "date": "05-19-2026",
  "time": "09:00:00"
}`,
      },
    ],
  },
  {
    id: "mcp-tools-nearby",
    title: "Nearby Transit",
    tools: [
      {
        name: "nearby___bus_stops",
        category: "Nearby",
        categoryKey: "nearby",
        description:
          "Find bus stops within a given radius of a coordinate. Returns bus stop code, name, road name, and exact coordinates for each stop found. Default radius is 2km.",
        params: [
          {
            name: "latitude",
            required: true,
            parts: [{ t: "text", v: "WGS84 latitude (decimal degrees)" }],
          },
          {
            name: "longitude",
            required: true,
            parts: [{ t: "text", v: "WGS84 longitude (decimal degrees)" }],
          },
          {
            name: "radius_in_meters",
            required: false,
            parts: [{ t: "text", v: "Default 2000 m, maximum 5000 m" }],
          },
        ],
        example: `{
  "latitude": 1.3521,
  "longitude": 103.8198,
  "radius_in_meters": 1000
}`,
      },
      {
        name: "nearby___mrt_stations",
        category: "Nearby",
        categoryKey: "nearby",
        description:
          "Find MRT and LRT stations within a given radius of a coordinate. Returns station name, line, and coordinates. Default radius is 2km.",
        params: [
          {
            name: "latitude",
            required: true,
            parts: [{ t: "text", v: "WGS84 latitude (decimal degrees)" }],
          },
          {
            name: "longitude",
            required: true,
            parts: [{ t: "text", v: "WGS84 longitude (decimal degrees)" }],
          },
          {
            name: "radius_in_meters",
            required: false,
            parts: [{ t: "text", v: "Default 2000 m, maximum 5000 m" }],
          },
        ],
        example: `{
  "latitude": 1.3521,
  "longitude": 103.8198,
  "radius_in_meters": 1000
}`,
      },
    ],
  },
  {
    id: "mcp-tools-thematic",
    title: "Thematic Datasets",
    tools: [
      {
        name: "thematic___allThemes",
        category: "Thematic",
        categoryKey: "thematic",
        description: [
          {
            t: "text",
            v: "List all available thematic map datasets and layers — schools, parks, hawker centres, carparks, hospitals, and more. Call this first to discover valid ",
          },
          { t: "chip", v: "queryName" },
          { t: "text", v: " values for " },
          { t: "chip", v: "thematic___themeLayer" },
          { t: "text", v: "." },
        ],
        params: [
          {
            name: "moreInfo",
            required: false,
            parts: [
              { t: "chip", v: "Y" },
              { t: "text", v: " for extended metadata · " },
              { t: "chip", v: "N" },
              { t: "text", v: " for basic listing" },
            ],
          },
        ],
        example: `{
  "moreInfo": "Y"
}`,
      },
      {
        name: "thematic___themeLayer",
        category: "Thematic",
        categoryKey: "thematic",
        description:
          "Fetch all records for a specific thematic dataset by its query name. Optionally filter to a bounding box to retrieve only records within a map area.",
        params: [
          {
            name: "queryName",
            required: true,
            parts: [
              { t: "text", v: "Theme identifier from" },
              { t: "chip", v: "thematic___allThemes" },
            ],
          },
          {
            name: "extents",
            required: false,
            parts: [
              { t: "text", v: "Bounding box as" },
              { t: "chip", v: '"minLat,minLng,maxLat,maxLng"' },
            ],
          },
        ],
        example: `{
  "queryName": "hawkercentre",
  "extents": "1.28,103.82,1.36,103.90"
}`,
      },
    ],
  },
  {
    id: "mcp-tools-population",
    title: "Population & Planning Areas",
    tools: [
      {
        kind: "family",
        name: "Planning areas",
        category: "Population",
        categoryKey: "population",
        description: [
          {
            t: "text",
            v: "Look up Singapore\u2019s planning areas by name, boundary or location. Start here if you need the exact area name for the census tools below. Boundary years: ",
          },
          { t: "chip", v: "1998" },
          { t: "text", v: ", " },
          { t: "chip", v: "2008" },
          { t: "text", v: ", " },
          { t: "chip", v: "2014" },
          { t: "text", v: ", " },
          { t: "chip", v: "2019" },
          { t: "text", v: ". If " },
          { t: "chip", v: "year" },
          {
            t: "text",
            v: " is omitted where optional, the latest boundaries are returned.",
          },
        ],
        columns: [
          { key: "name", label: "Tool", width: "36%" },
          { key: "returns", label: "Returns", width: "40%" },
          { key: "params", label: "Parameters", width: "24%" },
        ],
        rows: [
          {
            name: "popquery___getPlanningareaNames",
            returns:
              "IDs and official names of all planning areas. Use these names in the statistics tools.",
            params: [{ v: "year", optional: true }],
          },
          {
            name: "popquery___getAllPlanningarea",
            returns:
              "All planning areas with their GeoJSON geometry (as a JSON-encoded string).",
            params: [{ v: "year", optional: true }],
          },
          {
            name: "popquery___getPlanningarea",
            returns:
              "The planning area that contains a WGS84 point, with its GeoJSON MultiPolygon.",
            params: ["latitude", "longitude", "year"],
          },
        ],
        exampleTool: "popquery___getPlanningarea",
        example: `{
  "latitude": 1.2833,
  "longitude": 103.8607,
  "year": 2019
}`,
      },
      {
        kind: "family",
        name: "Census statistics by planning area",
        category: "Population",
        categoryKey: "population",
        description:
          "Nineteen tools that return census statistics for one planning area. They all take the same two required parameters, so once you know one you know them all.",
        sharedParams: [
          {
            name: "planningArea",
            required: true,
            parts: [
              { t: "text", v: "Official planning-area name, e.g." },
              { t: "chip", v: "Bedok" },
              {
                t: "text",
                v: ". Not case-sensitive, but no leading or trailing spaces. Not an address or postal code. Use",
              },
              { t: "chip", v: "popquery___getPlanningareaNames" },
              { t: "text", v: "for valid names." },
            ],
          },
          {
            name: "year",
            required: true,
            parts: [
              { t: "text", v: "Census year:" },
              { t: "chip", v: "2000" },
              { t: "text", v: "," },
              { t: "chip", v: "2010" },
              { t: "text", v: "," },
              { t: "chip", v: "2015" },
              { t: "text", v: "or" },
              { t: "chip", v: "2020" },
            ],
          },
          {
            name: "gender",
            required: false,
            parts: [
              { t: "chip", v: "male" },
              { t: "text", v: "or" },
              { t: "chip", v: "female" },
              { t: "text", v: ". Only on the four tools marked" },
              { t: "chip", v: "gender" },
              { t: "text", v: "below." },
            ],
          },
        ],
        tables: [
          {
            title: "Demographics",
            columns: [
              { key: "name", label: "Tool", width: "48%" },
              { key: "returns", label: "Returns", width: "37%" },
              { key: "optional", label: "Optional", width: "15%" },
            ],
            rows: [
              {
                name: "popquery___getPopulationAgeGroup",
                returns: "Population by age group",
                optional: ["gender"],
              },
              {
                name: "popquery___getEthnicGroup",
                returns: "Population by ethnic group",
                optional: ["gender"],
              },
              {
                name: "popquery___getMaritalStatus",
                returns: "Marital status",
                optional: ["gender"],
              },
              { name: "popquery___getReligion", returns: "Religion" },
              {
                name: "popquery___getLanguageLiterate",
                returns: "Language literacy",
              },
              {
                name: "popquery___getSpokenAtHome",
                returns: "Language spoken at home",
              },
            ],
          },
          {
            title: "Households & Housing",
            columns: [
              { key: "name", label: "Tool", width: "48%" },
              { key: "returns", label: "Returns", width: "37%" },
              { key: "optional", label: "Optional", width: "15%" },
            ],
            rows: [
              { name: "popquery___getHouseholdSize", returns: "Household size" },
              {
                name: "popquery___getHouseholdStructure",
                returns: "Household structure",
              },
              { name: "popquery___getTenancy", returns: "Household tenancy" },
              {
                name: "popquery___getTypeOfDwellingHousehold",
                returns: "Household counts by dwelling type",
              },
              {
                name: "popquery___getTypeOfDwellingPop",
                returns: "Population counts by dwelling type",
              },
              {
                name: "popquery___getHouseholdMonthlyIncomeWork",
                returns: "Household monthly income from work",
              },
            ],
          },
          {
            title: "Work, education & transport",
            columns: [
              { key: "name", label: "Tool", width: "48%" },
              { key: "returns", label: "Returns", width: "37%" },
              { key: "optional", label: "Optional", width: "15%" },
            ],
            rows: [
              {
                name: "popquery___getEconomicStatus",
                returns: "Economic status",
                optional: ["gender"],
              },
              {
                name: "popquery___getIncomeFromWork",
                returns: "Income from work",
              },
              { name: "popquery___getIndustry", returns: "Industry" },
              { name: "popquery___getOccupation", returns: "Occupation" },
              {
                name: "popquery___getEducationAttending",
                returns: "Education attending",
              },
              {
                name: "popquery___getModeOfTransportWork",
                returns: "Mode of transport to work",
              },
              {
                name: "popquery___getModeOfTransportSchool",
                returns: "Mode of transport to school",
              },
            ],
          },
        ],
        exampleTool: "popquery___getPopulationAgeGroup",
        example: `{
  "planningArea": "Bedok",
  "year": 2020,
  "gender": "female"
}`,
      },
    ],
  },
];

export default TOOL_GROUPS;